// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Landbridge — all the static/derived HTML: menu, help, about, and the results
// screens. The results always show EVERY player's route and, for a route mode, the
// game's OPTIMAL route — "what everyone missed" (principle #9).

import { escapeHtml } from './dom';
import { country, shortestPath, SOURCE } from './data';
import { MODE_IDS, modeOf, type Mode } from './modes';
import { hops, overPar, type GameState } from './game';
import type { PeerResult } from './race';

export const ATTRIB = `Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>`;

function name(id: string): string {
  return country(id)?.name ?? id;
}

function routeText(ids: string[]): string {
  return ids.map((id) => escapeHtml(name(id))).join(' <span class="arw">→</span> ');
}

function gradeLabel(state: GameState): string {
  if (state.objective === 'chain') return '';
  if (!state.solved) return 'Route not completed';
  const over = overPar(state);
  return over === 0 ? 'Perfect route!' : `+${over} over the shortest route`;
}

export function menuHtml(o: { modeId: string; best: number; muted: boolean; dateStr: string }): string {
  const mode = modeOf(o.modeId);
  const bestLine = o.best > 0 ? `<p class="menu-best">Best: ${o.best}${mode.objective === 'chain' ? '-country chain' : ' streak'}</p>` : '';
  return `
    <section class="screen menu">
      <div class="brand">
        <div class="logo" aria-hidden="true">
          <svg viewBox="0 0 64 64" width="56" height="56">
            <circle cx="32" cy="32" r="21" fill="none" stroke="#486072" stroke-width="2.5"/>
            <path d="M32 11 C22 20 22 44 32 53 M32 11 C42 20 42 44 32 53 M11 32 h42" fill="none" stroke="#486072" stroke-width="2"/>
            <path d="M17 46 L26 38 L37 29 L47 20" fill="none" stroke="#35c9bd" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="17" cy="46" r="4.2" fill="#46cf72" stroke="#0c1c28" stroke-width="1.3"/>
            <circle cx="26" cy="38" r="3.1" fill="#35c9bd" stroke="#0c1c28" stroke-width="1.3"/>
            <circle cx="37" cy="29" r="3.1" fill="#35c9bd" stroke="#0c1c28" stroke-width="1.3"/>
            <circle cx="47" cy="20" r="4.2" fill="#f4b83f" stroke="#0c1c28" stroke-width="1.3"/>
          </svg>
        </div>
        <h1>Landbridge</h1>
        <p class="tagline">Hop country to country along shared land borders.</p>
      </div>

      <div class="modes" role="radiogroup" aria-label="Mode">
        ${MODE_IDS.map(
          (id) =>
            `<button class="chip${id === o.modeId ? ' on' : ''}" role="radio" aria-checked="${id === o.modeId}" data-mode="${id}">${modeOf(id).name}</button>`,
        ).join('')}
      </div>
      <p class="mode-blurb">${escapeHtml(mode.blurb)}</p>
      ${bestLine}

      <div class="menu-actions">
        <button class="btn primary" id="playRandom">Play</button>
        <button class="btn" id="playDaily">Daily challenge</button>
        <button class="btn" id="playFriends">Play with friends</button>
        <div class="menu-row">
          <button class="btn ghost" id="help">How to play</button>
          <button class="btn ghost" id="about">About</button>
          <button class="btn ghost" id="mute">${o.muted ? 'Sound: off' : 'Sound: on'}</button>
        </div>
      </div>
    </section>`;
}

export function helpHtml(): string {
  return `
    <button class="sheet-close" aria-label="Close">×</button>
    <h2>How to play</h2>
    <p>Get from the <strong>start</strong> country (green) to the <strong>destination</strong> (amber ⚑)
      by naming countries that share a <strong>land border</strong>, one hop at a time.</p>
    <ul class="help-list">
      <li>Type a country and pick it — if it touches your last one, it joins the chain.</li>
      <li>Name a country that <em>doesn’t</em> border your last one and you lose a life.</li>
      <li>Fewer hops is better — the game knows the shortest route and grades you against it.</li>
      <li><strong>Undo</strong> any hop for free, so the only real cost is not knowing a border.</li>
      <li>Stuck? Take one <strong>Hint</strong> per round — it names the best next country but still
        makes you play it — or <strong>Give up</strong> to end the round and see the shortest route.</li>
      <li>Islands with no land borders can’t be crossed (there are no ferries).</li>
    </ul>
    <p><strong>Chain</strong> mode has no destination — just build the longest border-chain you can,
      never repeating a country, before you get stuck.</p>
    <p class="help-note">Uses everyday countries from a public-domain map; tiny micro-states aren’t in
      play, so their larger neighbours simply border each other directly.</p>`;
}

export function aboutHtml(): string {
  return `
    <button class="sheet-close" aria-label="Close">×</button>
    <h2>About Landbridge</h2>
    <p>A world-map routing puzzle: walk overland from one country to another along real shared land
      borders, in as few hops as you can. Solo, a daily challenge everyone shares, or a live race
      against friends on the same pair.</p>
    <p>The map and every border come from <strong>${escapeHtml(SOURCE)}</strong> — facts and
      public-domain vectors, bundled into the page. No map tiles, no location APIs, nothing is
      fetched at play time, and it works offline.</p>
    <p><strong>Playing with friends</strong> is peer-to-peer over WebRTC: a public signalling relay
      only brokers the first connection between browsers, and no game data is ever stored on a server.
      No cookies, no tracking beyond an anonymous page-view count.</p>
    <p class="about-foot">${ATTRIB}</p>`;
}

/** A row in the live chain list (newest last). */
export function chainRowHtml(id: string, i: number, isStart: boolean, isDest: boolean, isTail: boolean): string {
  const role = isStart ? 'start' : isDest ? 'dest' : isTail ? 'tail' : 'link';
  const tag = isStart ? 'START' : isDest ? 'DEST' : '';
  return `<li class="chain-row ${role}">
    <span class="cr-num">${i === 0 ? '▶' : i}</span>
    <span class="cr-name">${escapeHtml(name(id))}</span>
    ${tag ? `<span class="cr-tag">${tag}</span>` : ''}
  </li>`;
}

export function soloResultsHtml(state: GameState, mode: Mode, best: number): string {
  const solved = state.solved;
  const isChain = mode.objective === 'chain';
  const headline = isChain
    ? `${state.chain.length}-country chain`
    : solved
      ? gradeLabel(state)
      : state.reason === 'gaveup'
        ? 'Gave up'
        : 'Out of lives';
  const outcome = isChain
    ? state.reason === 'stuck'
      ? 'No new bordering country left — you were boxed in.'
      : state.reason === 'gaveup'
        ? 'You stopped here.'
        : 'Ran out of lives.'
    : solved
      ? `${hops(state)} hop${hops(state) === 1 ? '' : 's'} · shortest is ${state.par} · ${state.misfires} misfire${state.misfires === 1 ? '' : 's'}`
      : `You reached ${hops(state)} hop${hops(state) === 1 ? '' : 's'}. The shortest route was ${state.par}.`;
  // A hinted round is never reported as an unqualified result.
  const sub = state.hintId ? `${outcome} · hint used` : outcome;

  const optimal = !isChain ? shortestPath(state.startId, state.destId!) : [];
  const optimalBlock =
    optimal.length > 1
      ? `<div class="opt-route"><span class="opt-label">Shortest route (${optimal.length - 1} hops)</span>
          <p class="route-text">${routeText(optimal)}</p></div>`
      : '';

  return `
    <section class="screen results solo">
      <h2 class="res-headline ${solved || isChain ? 'good' : 'bad'}">${escapeHtml(headline)}</h2>
      <p class="res-sub">${escapeHtml(sub)}</p>
      <div class="your-route">
        <span class="opt-label">Your route</span>
        <p class="route-text">${routeText(state.chain)}</p>
      </div>
      ${optimalBlock}
      ${best > 0 ? `<p class="res-best">Best: ${best}${isChain ? '-country chain' : ' streak'}</p>` : ''}
    </section>`;
}

export function multiResultsHtml(
  ranked: PeerResult[],
  selfSeat: number | null,
  mode: Mode,
  startId: string,
  destId: string | null,
  tally: number[],
): string {
  const isChain = mode.objective === 'chain';
  const rows = ranked
    .map((r, i) => {
      const me = r.seat === selfSeat;
      let detail: string;
      if (r.left) detail = 'left';
      else if (isChain) detail = `${r.chainLen}-country chain${r.misfires ? ` · ${r.misfires} miss` : ''}`;
      else if (r.solved) detail = `${r.hops} hop${r.hops === 1 ? '' : 's'}${r.misfires ? ` · ${r.misfires} miss` : ''}`;
      else if (r.over) detail = 'did not finish';
      else detail = 'still routing…';
      const trail = r.trail.length > 1 ? `<p class="route-text mini">${routeText(r.trail)}</p>` : '';
      const wins = tally[r.seat] ? `<span class="pr-wins">${tally[r.seat]}★</span>` : '';
      return `<li class="pr-row${me ? ' me' : ''}${r.left ? ' left' : ''}">
        <span class="pr-rank">${i + 1}</span>
        <span class="pr-name">${escapeHtml(r.name)}${me ? ' <span class="pr-you">you</span>' : ''}</span>
        <span class="pr-detail">${escapeHtml(detail)}</span>
        ${wins}
        ${trail}
      </li>`;
    })
    .join('');

  const optimal = !isChain && destId ? shortestPath(startId, destId) : [];
  const optimalBlock =
    optimal.length > 1
      ? `<div class="opt-route"><span class="opt-label">Shortest route (${optimal.length - 1} hops)</span>
          <p class="route-text">${routeText(optimal)}</p></div>`
      : '';

  const head = isChain
    ? `From <strong>${escapeHtml(name(startId))}</strong> — longest chain wins`
    : `<strong>${escapeHtml(name(startId))}</strong> <span class="arw">→</span> <strong>${escapeHtml(name(destId ?? ''))}</strong>`;

  return `
    <h2 class="res-headline">Results</h2>
    <p class="res-sub">${head}</p>
    <ul class="pr-list">${rows}</ul>
    ${optimalBlock}`;
}
